import { OFFLINE_GAMES_ENABLED } from "./featureEnabled";
import { userIdFromToken } from "./gameFiles";
// Which games are downloaded for offline play (the files live in Cache Storage).
// Each entry lists the accounts that downloaded it on this device ("owners"):
// another account signed in on the same device doesn't get it (AK-118).
const STORAGE_KEY = "akedo_offline_games_v1";

const read = () => {
	try {
		const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
		return value && typeof value === "object" ? value : {};
	} catch (_) {
		return {};
	}
};

const write = (games) => {
	localStorage.setItem(STORAGE_KEY, JSON.stringify(games));
	window.dispatchEvent(new Event("akedo-offline-games-changed"));
};

const currentUserId = () => userIdFromToken(localStorage.getItem("token") || "");

// Entries saved before owners existed belong to no one: their account is unknown.
const ownedBy = (game, userId) => Boolean(userId) && Array.isArray(game?.owners) && game.owners.includes(userId);

export const listOfflineGames = () => {
	if (!OFFLINE_GAMES_ENABLED) return [];
	const userId = currentUserId();
	return Object.values(read())
		.filter((game) => ownedBy(game, userId))
		.sort((a, b) => (b.downloadedAt || 0) - (a.downloadedAt || 0));
};

export const getOfflineGame = (appId) => {
	if (!OFFLINE_GAMES_ENABLED) return null;
	const game = read()[appId];
	return ownedBy(game, currentUserId()) ? game : null;
};

export const saveOfflineGame = (game) => {
	const games = read();
	const owners = new Set([...(games[game.id]?.owners || []), currentUserId()].filter(Boolean));
	write({ ...games, [game.id]: { ...game, owners: [...owners] } });
};

// Removes the game for the signed-in account. Returns true when no other account
// on this device has it, so its files can be deleted too.
export const removeOfflineGameEntry = (appId) => {
	const games = read();
	const owners = (games[appId]?.owners || []).filter((id) => id !== currentUserId());
	if (owners.length) games[appId] = { ...games[appId], owners };
	else delete games[appId];
	write(games);
	return owners.length === 0;
};

// Does another account on this device have this game's files?
export const isDownloadedByAnotherAccount = (appId) => {
	const userId = currentUserId();
	return (read()[appId]?.owners || []).some((id) => id !== userId);
};

// A guest who connects an account keeps their data, downloads included.
export const transferOfflineGames = (fromUserId, toUserId) => {
	if (!fromUserId || !toUserId || fromUserId === toUserId) return;
	const games = read();
	let changed = false;
	Object.values(games).forEach((game) => {
		if (ownedBy(game, fromUserId) && !ownedBy(game, toUserId)) {
			games[game.id] = { ...game, owners: [...game.owners, toUserId] };
			changed = true;
		}
	});
	if (changed) write(games);
};

// Offline mode (and its service worker) is on once any account on this device downloaded a game.
export const hasOfflineGames = () => OFFLINE_GAMES_ENABLED && Object.keys(read()).length > 0;
